import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Form, Input, Button, Avatar, Upload, message } from "antd";
import { ArrowLeftOutlined, UserOutlined, CameraOutlined } from "@ant-design/icons";
import { updateMyProfile, uploadMyAvatar, deleteMyAvatar } from "../services/api";

/**
 * Self-service profile page: a clinician can edit their own first/middle/last name.
 * Email and roles are shown read-only. Profile picture is a separate later task.
 *
 * Props:
 *   options  — the already-loaded current-user object from App.js state.
 *              Has first_name, middle_name, last_name, email, roles.
 *              May be {} initially (don't crash; let the user type).
 *   onSaved  — optional callback fired with the updated clinician object after
 *              a successful save, so the header name updates immediately.
 */
export const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const MAX_AVATAR_BYTES = 5 * 1024 * 1024; // 5 MB

/**
 * Pure validation helper for avatar file selection.
 * Returns { ok: true } on success, or { ok: false, reason: "type" | "size" }.
 *
 * @param {File} file
 * @param {string[]} allowedTypes  - MIME types to accept
 * @param {number}   maxBytes      - inclusive byte ceiling
 */
export function validateAvatarFile(file, allowedTypes, maxBytes) {
  if (!allowedTypes.includes(file.type)) {
    return { ok: false, reason: "type" };
  }
  if (file.size > maxBytes) {
    return { ok: false, reason: "size" };
  }
  return { ok: true };
}

export default function Profile({ options = {}, onSaved }) {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  // avatarUrl mirrors options.avatar_url but can be updated optimistically.
  const [avatarUrl, setAvatarUrl] = useState(options?.avatar_url || null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarRemoving, setAvatarRemoving] = useState(false);

  // Keep local avatar in sync when the options prop changes externally.
  useEffect(() => {
    setAvatarUrl(options?.avatar_url || null);
  }, [options?.avatar_url]);

  // Populate form whenever options loads (or changes, e.g. after a save).
  useEffect(() => {
    form.setFieldsValue({
      first_name: options.first_name || "",
      middle_name: options.middle_name || "",
      last_name: options.last_name || "",
    });
  }, [form, options]);

  const onSave = async (values) => {
    setSaving(true);
    const payload = {
      first_name: values.first_name,
      middle_name: values.middle_name,
      last_name: values.last_name,
    };
    try {
      const response = await updateMyProfile(payload);
      // Notify parent so the header name updates immediately.
      // Fall back to the submitted fields if the backend returns nothing.
      onSaved?.(response ?? payload);
      message.success("Profile updated.");
    } catch (e) {
      message.error(
        e?.response?.data?.detail || e?.message || "Failed to update profile."
      );
    } finally {
      setSaving(false);
    }
  };

  // Client-side validation before the upload fires.
  // Returning true allows antd to proceed to customRequest.
  // Returning Upload.LIST_IGNORE rejects the file and prevents customRequest.
  const beforeUpload = (file) => {
    const result = validateAvatarFile(file, ALLOWED_TYPES, MAX_AVATAR_BYTES);
    if (!result.ok) {
      message.error(
        result.reason === "size"
          ? "Image must be 5 MB or smaller."
          : "Only JPEG, PNG, or WebP images are allowed."
      );
      return Upload.LIST_IGNORE;
    }
    // Return true so antd v5 calls customRequest with this file.
    return true;
  };

  const handleAvatarUpload = async ({ file }) => {
    setAvatarUploading(true);
    try {
      const updated = await uploadMyAvatar(file);
      setAvatarUrl(updated?.avatar_url || avatarUrl);
      onSaved?.(updated);
      message.success("Profile picture updated.");
    } catch (e) {
      message.error(
        e?.response?.data?.detail || e?.message || "Failed to upload profile picture."
      );
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    setAvatarRemoving(true);
    try {
      const updated = await deleteMyAvatar();
      setAvatarUrl(null);
      onSaved?.(updated);
      message.success("Profile picture removed.");
    } catch (e) {
      message.error(
        e?.response?.data?.detail || e?.message || "Failed to remove profile picture."
      );
    } finally {
      setAvatarRemoving(false);
    }
  };

  const cardTitle = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Button
        size="small"
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/")}
        aria-label="Back to main menu"
      >
        Back
      </Button>
      <span>My Profile</span>
    </div>
  );

  const roles = Array.isArray(options.roles) ? options.roles.join(", ") : options.roles || "";

  return (
    <Card title={cardTitle} style={{ maxWidth: 480, margin: "24px auto" }}>
      {/* ---- Profile picture section ---- */}
      <div
        style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, marginBottom: 24 }}
        data-testid="avatar-section"
      >
        <Avatar
          src={avatarUrl || undefined}
          icon={!avatarUrl && <UserOutlined />}
          size={96}
          aria-label="Profile picture"
        />
        <Upload
          accept="image/png,image/jpeg,image/webp"
          maxCount={1}
          showUploadList={false}
          beforeUpload={beforeUpload}
          customRequest={handleAvatarUpload}
          disabled={avatarUploading}
        >
          <Button
            icon={<CameraOutlined />}
            loading={avatarUploading}
            aria-label="Upload profile picture"
          >
            {avatarUrl ? "Change photo" : "Upload photo"}
          </Button>
        </Upload>
        {avatarUrl && (
          <Button
            type="link"
            danger
            onClick={handleRemoveAvatar}
            loading={avatarRemoving}
            aria-label="Remove profile picture"
          >
            Remove photo
          </Button>
        )}
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onSave}
        data-testid="profile-form"
      >
        <Form.Item
          label="First name"
          name="first_name"
          rules={[{ required: true, message: "First name is required." }]}
        >
          <Input aria-label="First name" />
        </Form.Item>

        <Form.Item label="Middle name" name="middle_name">
          <Input aria-label="Middle name" />
        </Form.Item>

        <Form.Item
          label="Last name"
          name="last_name"
          rules={[{ required: true, message: "Last name is required." }]}
        >
          <Input aria-label="Last name" />
        </Form.Item>

        {/* Read-only fields */}
        <Form.Item label="Email">
          <Input value={options.email || ""} aria-label="Email" disabled />
        </Form.Item>

        <Form.Item label="Role">
          <Input value={roles} aria-label="Role" disabled />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={saving}
            aria-label="Save profile"
          >
            Save
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
}
